// @view/panels/workspace/WorkspaceSidebar.panel.tsx
import React from "react";
import { NavLink } from "react-router-dom";
import EmptyView from "@view/EmptyView";
import * as styles from "@styles/WorkspaceSidebar.css";
import { enabledFeatures, FeaturesEnum } from "@utils/enabledFeatures";
import { MessageCircle, LogOut, Settings, UserCircle } from "lucide-react";
import type { IOts } from "@ps-bridge/interop";
import type { Ok } from "@ps-bridge/ok";

export type WorkspaceMenuPhase = "open" | "closed";
export type WorkspaceNavLinkPhase = "enabled" | "disabled" | "hidden";
export type WorkspaceNavLayoutPhase = "hidden" | "single" | "group";
export type WorkspaceNavSubNavPhase = "hidden" | "visible";
export type WorkspaceNavFeatureGate = "always" | "statementClassifier";

export type WorkspaceNavChild = {
  key: string;
  label: string;
  to: string;
  icon: React.ComponentType<{ size: number }>;
  featureGate: WorkspaceNavFeatureGate;
};

export type WorkspaceNavItem = {
  key: string;
  label: string;
  to: string;
  icon: React.ComponentType<{ size: number }>;
  linkPhase: WorkspaceNavLinkPhase;
  layoutPhase: WorkspaceNavLayoutPhase;
  subNavPhase: WorkspaceNavSubNavPhase;
  children: WorkspaceNavChild[];
};

type WorkspaceSidebarPanelProps = {
  accountName: string;
  organizationName: string;
  fullName: string;
  navItems: WorkspaceNavItem[];
  pageCounterPath: string;
  menuPhase: WorkspaceMenuPhase;
  runIO: (action: IOts<Ok>) => Ok;
  onMenuToggle: IOts<Ok>;
  onAccountSettings: IOts<Ok>;
  onFeedback: IOts<Ok>;
  onLogout: IOts<Ok>;
};

const WorkspaceSidebarPanel: React.FC<WorkspaceSidebarPanelProps> = ({
  accountName,
  organizationName,
  fullName,
  navItems,
  pageCounterPath,
  menuPhase,
  runIO,
  onMenuToggle,
  onAccountSettings,
  onFeedback,
  onLogout,
}) => {
  const childFeatureGateVisible = {
    always: true,
    statementClassifier: enabledFeatures[FeaturesEnum.StatementClassifier],
  } as const satisfies Record<WorkspaceNavFeatureGate, boolean>;

  const renderMenu = {
    open: (
      <div
        className={styles.accountPopover}
        role="menu"
        onClick={(event) => event.stopPropagation()}
      >
        <div className={styles.accountPopoverHeader}>{fullName}</div>
        <button
          className={styles.accountPopoverItem}
          onClick={() => runIO(onAccountSettings)}
        >
          <Settings size={16} />
          User Settings
        </button>
        <button
          className={styles.accountPopoverItem}
          onClick={() => runIO(onFeedback)}
        >
          <MessageCircle size={16} />
          Give Feedback
        </button>
        <button
          className={styles.accountPopoverItemDanger}
          onClick={() => runIO(onLogout)}
        >
          <LogOut size={16} />
          Logout
        </button>
      </div>
    ),
    closed: <EmptyView />,
  } as const satisfies Record<WorkspaceMenuPhase, React.ReactElement>;

  return (
    <aside className={styles.sidebar}>
      <div className={styles.brand}>
        <img
          className={styles.brandLogo}
          src="/assets/ouroboros_logo_flat.png"
          alt="Ouroboros"
        />
        <span className={styles.brandText}>Ouroboros AI</span>
      </div>

      <div>
        <div className={styles.sectionTitle}>Workspace</div>
        <nav className={styles.nav}>
          {navItems.map(({ key, label, to, icon: Icon, linkPhase, layoutPhase, subNavPhase, children }) => {
            const linkView = {
              enabled: (
                <NavLink
                  key={key}
                  to={to}
                  className={({ isActive }) =>
                    [styles.navLink, isActive ? styles.navLinkActive : ""]
                      .filter(Boolean)
                      .join(" ")
                  }
                >
                  <Icon size={18} />
                  <span>{label}</span>
                </NavLink>
              ),
              disabled: (
                <div key={key} className={`${styles.navLink} ${styles.navLinkDisabled}`}>
                  <Icon size={18} />
                  <span>{label}</span>
                </div>
              ),
              hidden: <EmptyView />,
            } as const satisfies Record<WorkspaceNavLinkPhase, React.ReactElement>;

            const subNavView = {
              hidden: <EmptyView />,
              visible: (
                <div className={styles.subNav}>
                  {children
                    .filter((child) => childFeatureGateVisible[child.featureGate])
                    .map(({ key: childKey, label: childLabel, to: childTo, icon: ChildIcon }) => (
                    <NavLink
                      key={childKey}
                      to={childTo}
                      className={({ isActive }) =>
                        [styles.subNavLink, isActive ? styles.subNavLinkActive : ""]
                          .filter(Boolean)
                          .join(" ")
                      }
                    >
                      <ChildIcon size={16} />
                      <span>{childLabel}</span>
                    </NavLink>
                  ))}
                </div>
              ),
            } as const satisfies Record<WorkspaceNavSubNavPhase, React.ReactElement>;

            const layoutView = {
              hidden: <EmptyView />,
              single: linkView[linkPhase],
              group: (
                <div key={`${key}-group`} className={styles.navGroup}>
                  {linkView[linkPhase]}
                  {subNavView[subNavPhase]}
                </div>
              ),
            } as const satisfies Record<WorkspaceNavLayoutPhase, React.ReactElement>;

            return layoutView[layoutPhase];
          })}
        </nav>
      </div>

      <NavLink
        to={pageCounterPath}
        className={({ isActive }) =>
          [styles.pageCounterWidget, isActive ? styles.pageCounterWidgetActive : ""]
            .filter(Boolean)
            .join(" ")
        }
      >
        <span className={styles.pageCounterWidgetEyebrow}>Others</span>
        <span className={styles.pageCounterWidgetTitle}>Page Counter</span>
        <span className={styles.pageCounterWidgetCopy}>Upload a PDF to count pages and preview billing.</span>
      </NavLink>

      <div className={styles.bottomZone}>
        <div className={styles.accountMenu}>
          <button
            className={styles.accountWidget}
            onClick={(event) => {
              event.stopPropagation();
              runIO(onMenuToggle);
            }}
          >
            <UserCircle size={20} />
            <div className={styles.accountText}>
              <span className={styles.accountName}>{accountName}</span>
              <span className={styles.accountMeta}>
                <span className={styles.accountOrg}>{organizationName}</span>
                <span className={styles.accountMetaLabel}>Account</span>
              </span>
            </div>
          </button>
          {renderMenu[menuPhase]}
        </div>
        <div className={styles.footer}>© Ouroboros AI 2026</div>
      </div>
    </aside>
  );
};

export default WorkspaceSidebarPanel;
